import { useEffect, useRef, useState } from 'preact/hooks'
import type { ComponentChildren } from 'preact'
import { ArrowRight, Info, Loader2, Sparkles } from 'lucide-preact'
import { useLandingPageTemplates } from '@/hooks/useLandingPageTemplates'
import type { LandingPageTemplateListItemDto } from '@/types/landingPage'
import { TemplateThumbnail } from '@/components/landingPageTemplates/TemplateThumbnail'
import { signedOffText } from '@/components/landingPageTemplates/templateText'
import { TemplateChooser } from './TemplateChooser'

/** How many of the most recently saved templates the list offers before "Show all". */
const RECENT_COUNT = 4

const SECTION_HEADING = 'text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2'

interface TemplatePickerProps {
    workspaceId: string
    webId: string
    /** The webs this one can start from, in words: "live Online Event", "Download". */
    compatibleLabel: string
    /** Whether this web is an online event, which decides whether the wording to swap is an event's. */
    isOnlineEvent: boolean
    /** The template the page will start from, or null to start fresh. */
    selected: LandingPageTemplateListItemDto | null
    onSelect: (template: LandingPageTemplateListItemDto | null) => void
    disabled?: boolean
}

/**
 * "Start from a template", the first section of the landing page builder before anything is built (ORB-1103). Start
 * fresh is the default; next to it are the most recently saved templates this web can start from, as the server judges
 * it, and "Show all" opens the full chooser with a preview. A template picked there that is not one of the recent ones
 * is pinned above them.
 */
export function TemplatePicker({ workspaceId, webId, compatibleLabel, isOnlineEvent, selected, onSelect, disabled }: TemplatePickerProps) {
    const { templates, loading, error } = useLandingPageTemplates(workspaceId, webId)
    const [pinned, setPinned] = useState<LandingPageTemplateListItemDto | null>(null)
    const [chooserOpen, setChooserOpen] = useState(false)
    const listRef = useRef<HTMLUListElement>(null)
    // The row a template used from the chooser lands on, focused once the chooser is gone. An effect here runs after the
    // chooser's own cleanup, which hands focus back to "Show all".
    const [focusPick, setFocusPick] = useState<string | null>(null)

    useEffect(() => {
        if (chooserOpen || focusPick === null) return
        setFocusPick(null)
        const rows = Array.from(listRef.current?.querySelectorAll<HTMLElement>('[data-web]') ?? [])
        rows.find(row => row.dataset.web === focusPick)?.querySelector<HTMLButtonElement>('button')?.focus()
    }, [chooserOpen, focusPick])

    if (!templates) {
        return (
            <div>
                <h3 className={SECTION_HEADING}>Start from a template</h3>
                {loading || !error ? (
                    <p className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
                        Loading templates…
                    </p>
                ) : (
                    <p className="text-sm text-slate-500 dark:text-slate-400">Templates could not load, so this page starts fresh. {error}</p>
                )}
            </div>
        )
    }

    if (templates.length === 0) {
        return (
            <div>
                <h3 className={SECTION_HEADING}>Start from a template</h3>
                <p className="flex items-start gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                    <Info className="w-3.5 h-3.5 mt-px shrink-0 text-slate-400 dark:text-slate-500" aria-hidden="true" />
                    <span>
                        No templates from {compatibleLabel} webs yet. Once a landing page is signed off, its web’s Landing
                        Page tab can save it as one.
                    </span>
                </p>
            </div>
        )
    }

    const recent = templates.slice(0, RECENT_COUNT)
    const offered = pinned && !recent.some(template => template.WebId === pinned.WebId) ? [pinned, ...recent] : recent

    const useChosen = (template: LandingPageTemplateListItemDto) => {
        setPinned(template)
        onSelect(template)
        setChooserOpen(false)
        setFocusPick(template.WebId)
    }

    return (
        <div>
            <h3 className={SECTION_HEADING}>Start from a template</h3>
            <ul ref={listRef} className="flex flex-col gap-2">
                <li>
                    <PickRow selected={!selected} disabled={disabled} onClick={() => onSelect(null)} art={(
                        <span className="w-24 h-[60px] flex items-center justify-center rounded border border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-400">
                            <Sparkles className="w-5 h-5" aria-hidden="true" />
                        </span>
                    )}>
                        <PickText name="Start fresh" sub="Webster designs a new page" selected={!selected} />
                    </PickRow>
                </li>
                {offered.map(template => {
                    const isSelected = selected?.WebId === template.WebId
                    return (
                        <li key={template.WebId} data-web={template.WebId}>
                            <PickRow
                                selected={isSelected}
                                disabled={disabled}
                                onClick={() => onSelect(template)}
                                art={<TemplateThumbnail workspaceId={workspaceId} template={template} hasPage size="row" />}
                                trailing={template.LatestSignedOffVersion && (
                                    <span className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                                        {signedOffText(template.LatestSignedOffVersion, false)}
                                    </span>
                                )}
                            >
                                <PickText name={template.Template.Name} sub={template.CampaignName} selected={isSelected} />
                            </PickRow>
                        </li>
                    )
                })}
            </ul>

            <button
                type="button"
                aria-haspopup="dialog"
                disabled={disabled}
                onClick={() => setChooserOpen(true)}
                className="mt-3 inline-flex items-center gap-1 rounded text-sm font-medium text-primary-700 dark:text-primary-400 hover:text-primary-800 hover:underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50"
            >
                {templates.length === 1 ? 'Preview the template' : `Show all ${templates.length} templates`}
                <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
            </button>

            {selected ? (
                <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
                    Copies the landing page and thank-you page of{' '}
                    <span className="font-medium text-slate-800 dark:text-slate-100">{selected.Template.Name}</span>
                    {selected.LatestSignedOffVersion && ` (${signedOffText(selected.LatestSignedOffVersion, false)})`}, skips
                    Webster’s design questions and swaps only the old {isOnlineEvent ? 'event' : 'page'}’s wording for this
                    web’s. Anything you type below is done in the same pass. The full side panel is back once it’s generated.
                </p>
            ) : (
                <p className="mt-3 flex items-start gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                    <Info className="w-3.5 h-3.5 mt-px shrink-0 text-slate-400 dark:text-slate-500" aria-hidden="true" />
                    <span>The most recently saved templates from {compatibleLabel} webs, like this one.</span>
                </p>
            )}

            {chooserOpen && (
                <TemplateChooser
                    workspaceId={workspaceId}
                    templates={templates}
                    initialWebId={selected?.WebId ?? null}
                    compatibleLabel={compatibleLabel}
                    onUse={useChosen}
                    onClose={() => setChooserOpen(false)}
                />
            )}
        </div>
    )
}

/**
 * One choice in the list. The thumbnail sits beside the button rather than inside it, because a frame is interactive
 * content and a button may not hold one; it ignores the pointer, so a click on it still lands on the button.
 */
function PickRow({ selected, disabled, onClick, art, trailing, children }: {
    selected: boolean
    disabled?: boolean
    onClick: () => void
    art: ComponentChildren
    trailing?: ComponentChildren
    children: ComponentChildren
}) {
    return (
        <div className="relative">
            <button
                type="button"
                aria-pressed={selected}
                disabled={disabled}
                onClick={onClick}
                className={`w-full min-h-[78px] flex items-center gap-4 pl-[120px] pr-4 py-2 rounded-lg border text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-60 disabled:cursor-not-allowed ${
                    selected
                        ? 'border-primary-600 ring-1 ring-primary-600 bg-primary-50 dark:bg-primary-950/40'
                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/60'
                }`}
            >
                <span className="min-w-0 flex-1">{children}</span>
                {trailing}
            </button>
            <span className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none">{art}</span>
        </div>
    )
}

function PickText({ name, sub, selected }: { name: string; sub: string; selected: boolean }) {
    return (
        <span className="flex flex-col min-w-0">
            <span className={`truncate text-sm font-medium ${selected ? 'text-primary-700 dark:text-primary-300' : 'text-slate-800 dark:text-slate-100'}`}>{name}</span>
            <span className="truncate text-xs text-slate-500 dark:text-slate-400">{sub}</span>
        </span>
    )
}
